import { reaction } from 'mobx';
import {
    ISlideRenderStateManager, IStatePointEnum,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import imageCache from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache';
import { getQuery } from 'sutils';

declare global {
    interface Window {
        // 当前页渲染完成，promise返回当前页的index
        currentSlideRenderCompletePromise: Promise<number>;
        // 所有页渲染完成
        allSlideRenderCompletePromise: Promise<unknown>;
        // 首屏渲染完成
        visibleSlideRenderCompletePromise: Promise<unknown>;
        // 服务实例
        renderStateNotification: SnapShotNotification;
    }
}

const SNAP_SHOT_ENV_KEY = 'isSnapshotEnv';
const SNAP_SHOT_ENV_VAL = '1';

export class SnapShotNotification {
    private slideRenderStateManager: ISlideRenderStateManager;
    private visibleSlideRenderCompletePromise: Promise<void>;
    private visibleSlideRenderCompleteResolve: () => void;
    private currentSlideRenderCompletePromise: Promise<number>;
    private currentSlideRenderCompleteResolve: (value: number) => void;
    private currentSlideKey: string;

    constructor(slideRenderStateManager: ISlideRenderStateManager) {
        if (getQuery(SNAP_SHOT_ENV_KEY) !== SNAP_SHOT_ENV_VAL) {
            return;
        }
        this.visibleSlideRenderCompletePromise = new Promise((resolve) => {
            this.visibleSlideRenderCompleteResolve = resolve;
        });
        this.currentSlideRenderCompletePromise = new Promise((resolve) => {
            this.currentSlideRenderCompleteResolve = resolve;
        });

        // window挂载，因为需要外部的puppeteer读取
        global.renderStateNotification = this;
        global.visibleSlideRenderCompletePromise = this.visibleSlideRenderCompletePromise;
        global.currentSlideRenderCompletePromise = this.currentSlideRenderCompletePromise;

        this.slideRenderStateManager = slideRenderStateManager;

        this.observerCurrentSlideChange();
        this.observerVisibleSlideComplete();
    }

    private notifyVisibleSlideLoaded = (): void => {
        if (!global.slideLoadState) {
            window.log.info('global.slideLoadState not exist');
            return;
        }
        global.slideLoadState.isAllSlideLoaded = true;
        window.log.info(`visible slide loaded: ${global.slideLoadState.listener}`);
        if (global.slideLoadState.listener) {
            global.slideLoadState.listener();
            global.slideLoadState.listener = undefined;
        }
    };

    /**
     * 监听屏幕内的slide以及对应的图片均已加载完成
     */
    private observerVisibleSlideComplete = async () => {
        await this.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise();
        await this.getVisibleSlideImgLoadCompletePromise();
        // 沿用旧逻辑，延迟1s再通知，确保完成（因为截图对时效要求不高，但是对渲染完成稳定要求很高）
        await new Promise(resolve => setTimeout(resolve, 1000));
        this.visibleSlideRenderCompleteResolve();
        this.notifyVisibleSlideLoaded();
    };

    /**
     * 监听当前页变更，如果当前页渲染完成则resolve
     */
    private observerCurrentSlideChange = async () => {
        const { selectionService } = await this.slideRenderStateManager.getServicesPromise();
        reaction(() => ({
            currentSlide: selectionService.currentSlide,
        }), async (obj) => {
            let currentSlide;
            if (!obj.currentSlide) {
                // 移动端列表，没有currentSlide，用第一页
                await this.slideRenderStateManager
                    .getStatePoints()[IStatePointEnum.firstSlideRender].getPointCompletePromise();
                currentSlide = selectionService.slides?.[0];
                if (!currentSlide || this.currentSlideKey === currentSlide.key) {
                    return;
                }
                this.currentSlideKey = currentSlide.key;
            } else {
                // 编辑模式
                currentSlide = obj.currentSlide;
                this.currentSlideKey = currentSlide.key;
                if (this.currentSlideKey) {
                    // currentSlideKey已存在说明，已经用过此promise了，当前页变更则要更新成新的promise
                    this.currentSlideRenderCompletePromise = new Promise((resolve) => {
                        this.currentSlideRenderCompleteResolve = resolve;
                    });
                    window.currentSlideRenderCompletePromise = this.currentSlideRenderCompletePromise;
                }
                await this.slideRenderStateManager
                    .getStatePoints()[IStatePointEnum.editSlideRender]
                    .getEditSlideRenderCompletePromiseByModel(currentSlide);
            }
            const index = currentSlide?.parent?.children.findIndex(e => e === currentSlide) || 0;
            await this.getImgLoadCompletePromiseByModel(currentSlide);
            // 沿用旧逻辑，延迟1s再通知，确保完成（因为截图对时效要求不高，但是对渲染完成稳定要求很高）
            await new Promise(resolve => setTimeout(resolve, 1000));
            this.currentSlideRenderCompleteResolve(index);
        }, { fireImmediately: true });
    };

    /**
     * 获取指定slide的所有图片加载完成promise
     * @param slide
     */
    private getImgLoadCompletePromiseByModel(slide: ISlideModel): Promise<void[]> {
        return imageCache.promiseModelTreeImgLoaded(slide);
    }

    /**
     * 获取所有可见slide的图片加载完成的时机的promise
     */
    private async getVisibleSlideImgLoadCompletePromise(): Promise<void[][]> {
        const { uiDataService } = await this.slideRenderStateManager.getServicesPromise();
        const visibleSlides = uiDataService?.presentation?.children
            .filter(e => this.slideRenderStateManager.getVisibleSlideKeyList().includes(e.key)) || [];
        return Promise.all(visibleSlides.map(e => this.getImgLoadCompletePromiseByModel(e)));
    }
}
